import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUserContext } from "../../contexts";
import { getAssetsConsumptions } from '../../lib/api';
import { Spinner } from '../Spinner';
import './Consumptions.scss';

const createDefaultFilters = () => ({
    startDate: '',
    endDate: '',
});

const createEmptyPagination = () => ({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    from: null,
    to: null,
});

const VERTICAL_PATHS = {
    videos: 'video-effects',
    footage: 'video-effects',
    graphics: 'stock-image-hd',
    photos: 'stock-image-hd',
    sounds: 'sounds-effects',
    audio: 'sounds-effects',
    music: 'royalty-free-music',
    environments: 'environments',
    materials: 'materials',
    objects: 'objects',
    '3d': 'objects',
    models: 'objects',
    default: 'video-effects'
};

const VERTICAL_THEMES = {
    videos: 'video',
    footage: 'video',
    graphics: 'graphics',
    photos: 'graphics',
    sounds: 'audio',
    audio: 'audio',
    music: 'audio',
    environments: 'environment',
    materials: 'materials',
    objects: 'objects',
    '3d': 'objects',
    models: 'objects'
};

const getMappedVerticalPath = (vertical = '') => {
    const key = vertical?.toLowerCase?.() || '';
    return VERTICAL_PATHS[key] || VERTICAL_PATHS.default;
};

const buildAssetUrl = (extra = {}) => {
    if (extra.url) {
        return extra.url;
    }
    if (!extra.slug) {
        return '#';
    }
    const path = getMappedVerticalPath(extra.vertical);
    return `https://www.productioncrate.com/${path}/${extra.slug}`;
};

const pickImagePreview = (previews = []) => {
    const images = previews.filter(preview => preview.type === 'image');
    const preference = ['lg', 'front', 'sprite', '2x', 'sm', 'xs'];
    for (const variant of preference) {
        const found = images.find(preview => (preview.variant || '').toLowerCase() === variant);
        if (found) {
            return found;
        }
    }
    return images[0];
};

const getWaveformPreview = (previews = []) => {
    return previews.find(preview => preview.type === 'json' && (preview.variant || '').toLowerCase() === 'waveform');
};

const buildPreviewUrl = (preview) => {
    if (!preview) {
        return '';
    }
    const host = preview.host || '';
    const path = preview.path || '';
    return `${host}${path}`;
};

const extractWaveformValues = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload.data)) {
        return payload.data;
    }
    if (Array.isArray(payload.samples)) {
        return payload.samples;
    }
    if (Array.isArray(payload.values)) {
        return payload.values;
    }
    if (Array.isArray(payload.waveform)) {
        return payload.waveform;
    }
    return [];
};

const formatDateParts = (dateString) => {
    if (!dateString) {
        return { dateLabel: '', timeLabel: '' };
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return { dateLabel: dateString, timeLabel: '' };
    }
    return {
        dateLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        timeLabel: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    };
};

const WaveformPreview = ({ values = [] }) => {
    if (!values.length) {
        return <div className="consumption-card__waveform consumption-card__waveform--empty" />;
    }
    const barsToShow = 60;
    const chunkSize = Math.max(Math.floor(values.length / barsToShow), 1);
    const bars = [];
    for (let i = 0; i < barsToShow; i += 1) {
        const start = i * chunkSize;
        const slice = values.slice(start, start + chunkSize);
        if (!slice.length) {
            break;
        }
        const avg = slice.reduce((acc, val) => acc + Math.abs(val), 0) / slice.length;
        bars.push(avg);
    }
    const max = bars.reduce((acc, val) => Math.max(acc, val), 0) || 1;
    return (
        <div className="consumption-card__waveform">
            {bars.map((value, index) => (
                <span
                    key={`wave-${index}`}
                    style={{ height: `${(value / max) * 100}%` }}
                />
            ))}
        </div>
    );
};

const Consumptions = () => {
    const { user } = useUserContext();

    const [consumptions, setConsumptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [waveforms, setWaveforms] = useState({});
    const requestedWaveforms = useRef(new Set());
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(createEmptyPagination);
    const [filters, setFilters] = useState(createDefaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!user) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setErrorMessage('');

        getAssetsConsumptions({
            page,
            startDate: appliedFilters.startDate || undefined,
            endDate: appliedFilters.endDate || undefined,
        })
            .then(payload => {
                if (cancelled) {
                    return;
                }

                const nextConsumptions = Array.isArray(payload?.data) ? payload.data : [];
                const meta = payload?.meta ?? {};
                const currentPage = Number(meta.current_page) || page;
                const lastPage = Number(meta.last_page) || 1;
                const total = Number(meta.total);

                setConsumptions(nextConsumptions);
                setPagination({
                    currentPage,
                    lastPage,
                    total: Number.isFinite(total) ? total : nextConsumptions.length,
                    from: meta.from ?? null,
                    to: meta.to ?? null,
                });

                if (page > lastPage) {
                    setPage(lastPage);
                }

                setLoading(false);
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setConsumptions([]);
                setPagination(createEmptyPagination());
                setErrorMessage('Could not load your download history. Please try again.');
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [user, page, appliedFilters.startDate, appliedFilters.endDate]);

    useEffect(() => {
        if (consumptions.length > 0) {
            return;
        }
        setWaveforms({});
        requestedWaveforms.current.clear();
    }, [consumptions.length]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        let cancelled = false;
        const audioItems = consumptions.filter(item => Boolean(getWaveformPreview(item?.extra_attributes?.previews || [])));
        audioItems.forEach(item => {
            if (requestedWaveforms.current.has(item.id)) {
                return;
            }
            const preview = getWaveformPreview(item.extra_attributes?.previews || []);
            if (!preview) {
                return;
            }
            const url = buildPreviewUrl(preview);
            if (!url) {
                return;
            }
            requestedWaveforms.current.add(item.id);
            fetch(url)
                .then(response => response.json())
                .then(payload => {
                    if (cancelled) {
                        return;
                    }
                    const values = extractWaveformValues(payload);
                    if (!values.length) {
                        return;
                    }
                    setWaveforms(prev => ({
                        ...prev,
                        [item.id]: values
                    }));
                })
                .catch(() => {
                    // ignore fetch errors, waveform is optional
                });
        });
        return () => {
            cancelled = true;
        };
    }, [consumptions]);

    const hasPendingFilterChanges = useMemo(() => (
        filters.startDate !== appliedFilters.startDate
        || filters.endDate !== appliedFilters.endDate
    ), [filters.startDate, filters.endDate, appliedFilters.startDate, appliedFilters.endDate]);

    const hasActiveFilters = Boolean(appliedFilters.startDate || appliedFilters.endDate);
    const hasDraftFilters = Boolean(filters.startDate || filters.endDate);
    const hasInvalidDateRange = Boolean(
        filters.startDate
        && filters.endDate
        && filters.startDate > filters.endDate
    );

    const currentPage = pagination.currentPage || 1;
    const lastPage = pagination.lastPage || 1;
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < lastPage;

    const totalLabel = useMemo(() => {
        const total = pagination.total || 0;
        if (!total) {
            return null;
        }
        if (pagination.from != null && pagination.to != null) {
            return `Showing ${pagination.from}-${pagination.to} of ${total}`;
        }
        return `${total} assets`;
    }, [pagination.from, pagination.to, pagination.total]);

    const handleFilterChange = (key) => (event) => {
        const value = event.target.value;
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleApplyFilters = () => {
        if (hasInvalidDateRange) {
            return;
        }
        setAppliedFilters({
            startDate: filters.startDate,
            endDate: filters.endDate,
        });
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters(createDefaultFilters());
        setAppliedFilters(createDefaultFilters());
        setPage(1);
    };

    const handlePreviousPage = () => {
        if (!hasPreviousPage) {
            return;
        }
        setPage(prev => Math.max(prev - 1, 1));
    };

    const handleNextPage = () => {
        if (!hasNextPage) {
            return;
        }
        setPage(prev => Math.min(prev + 1, lastPage));
    };

    return (
        <div className="consumptions row-section">
            <div className="title m-b-8">
                Assets Download History
            </div>
            <div className="consumptions__filters m-b-12">
                <label className="consumptions__filter-field">
                    <span>From</span>
                    <input
                        type="date"
                        value={filters.startDate}
                        onChange={handleFilterChange('startDate')}
                    />
                </label>
                <label className="consumptions__filter-field">
                    <span>To</span>
                    <input
                        type="date"
                        value={filters.endDate}
                        onChange={handleFilterChange('endDate')}
                    />
                </label>
                <div className="consumptions__filter-actions">
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={handleApplyFilters}
                        disabled={loading || hasInvalidDateRange || !hasPendingFilterChanges}
                    >
                        Apply
                    </button>
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={handleClearFilters}
                        disabled={loading || (!hasActiveFilters && !hasDraftFilters)}
                    >
                        Clear
                    </button>
                </div>
            </div>
            {hasInvalidDateRange && (
                <div className="consumptions__filter-error">
                    Start date cannot be after end date.
                </div>
            )}
            <div className="feat-list">
                {loading &&
                    <div className="loading visible m-t-16" style={{height: '300px'}}>
                        <Spinner />
                    </div>
                }
                {!loading && errorMessage && (
                    <div className="no-items m-t-16">
                        {errorMessage}
                    </div>
                )}
                {!loading && !errorMessage && consumptions.length === 0 &&
                    <div className="no-items m-t-16">
                        {hasActiveFilters
                            ? 'No assets found for the selected date range.'
                            : "You haven't downloaded any assets yet."}
                    </div>
                }
                {!loading && !errorMessage && consumptions.length > 0 && (
                    <>
                        <ul className="consumptions__grid">
                            {consumptions.map((item) => {
                                const vertical = item?.extra_attributes?.vertical || '';
                                const themeKey = VERTICAL_THEMES[vertical?.toLowerCase?.()] || 'default';
                                const preview = pickImagePreview(item?.extra_attributes?.previews || []);
                                const previewUrl = buildPreviewUrl(preview);
                                const assetUrl = buildAssetUrl(item.extra_attributes || {});
                                const { dateLabel, timeLabel } = formatDateParts(item.created_at);
                                const label = vertical ? vertical.replace(/-/g, ' ') : 'asset';
                                const level = item?.extra_attributes?.level;
                                const hasStar = level != null && String(level) !== '0';
                                const waveformValues = waveforms[item.id];
                                const isAudio = themeKey === 'audio';
                                const title = item?.extra_attributes?.title || 'Untitled asset';
                                return (
                                    <li key={`consumption-card-${item.id}`} className={`consumption-card consumption-card--${themeKey}`}>
                                        <div className="consumption-card__title-bar">
                                            <a
                                                href={assetUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="consumption-card__title-link"
                                            >
                                                {hasStar && (
                                                    <span className="consumption-card__title-star">
                                                        <span className="glyphicon glyphicon-star" />
                                                    </span>
                                                )}
                                                <span className="consumption-card__title-text">{title}</span>
                                            </a>
                                        </div>
                                        <a
                                            href={assetUrl}
                                            className="consumption-card__thumb-link"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <div className="consumption-card__thumb">
                                                {isAudio && <WaveformPreview values={waveformValues || []} />}
                                                {!isAudio && previewUrl && (
                                                    <img src={previewUrl} alt={item?.extra_attributes?.title || 'Asset preview'} />
                                                )}
                                                {!isAudio && !previewUrl && (
                                                    <div className="consumption-card__placeholder">
                                                        {label.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                        <div className="consumption-card__meta">
                                            <div className="consumption-card__header">
                                                <span className="consumption-card__vertical">{label}</span>
                                            </div>
                                            <div className="consumption-card__date">
                                                {dateLabel}
                                                {timeLabel && <span> · {timeLabel}</span>}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="consumptions__footer">
                            {totalLabel && (
                                <div className="consumptions__summary">
                                    {totalLabel}
                                </div>
                            )}
                            {lastPage > 1 && (
                                <div className="consumptions__cta">
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={handlePreviousPage}
                                        disabled={!hasPreviousPage || loading}
                                    >
                                        Previous
                                    </button>
                                    <span className="consumptions__pagination-status">
                                        Page {currentPage} of {lastPage}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={handleNextPage}
                                        disabled={!hasNextPage || loading}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Consumptions;
